'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// An inline image that opens a full-screen lightbox on click. `fullSrc` is the
// high-res version shown in the overlay (falls back to `src`). Closes on
// backdrop click, the ✕ button, or Escape.
export default function ZoomableImage({
  src,
  fullSrc,
  alt,
  className,
}: {
  src: string;
  fullSrc?: string;
  alt: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    // Prevent the page scrolling behind the lightbox.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onClick={() => setOpen(true)}
        className={`cursor-zoom-in transition-[filter] hover:brightness-[1.03] ${className ?? ''}`}
      />
      {open && mounted
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-label={alt}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[1150] flex items-center justify-center bg-black/85 p-4 md:p-8 cursor-zoom-out"
            >
              <img
                src={fullSrc ?? src}
                alt={alt}
                onClick={(e) => e.stopPropagation()}
                className="max-h-full max-w-full object-contain rounded-md shadow-2xl cursor-default"
              />
              <button
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="fixed right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white/90 transition hover:bg-white/20"
              >
                ×
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
